import React, { useEffect, useRef, useState } from 'react'
import { sprintf, _n } from '@wordpress/i18n'
import SbUtils from '../../../Utils/SbUtils'
import Button from '../../Common/Button'
import { useAnnouncer } from '../../../a11y/Announcer'
import './LoadMoreButton.css'

const LoadMoreButton = (props) => {

    const { feedSettings, feedId, isFeedEditor, posts, setPosts, postsList, setPostsList, postsPerPage } = props;
    const layout = feedSettings?.layout || 'grid';

    const [page, setPage] = useState(1);
    const [loadCount, setLoadCount] = useState(1);
    const [offset, setOffset] = useState(postsPerPage);
    const [nextPage, setNextPage] = useState(props?.nextPage || null);
    const [showLoadButton, setShowLoadButton] = useState(props?.nextPage || null);

    // Closes finding v3 S9: pre-fix, the "Load more" interaction silently
    // appended posts to the DOM with no AT signal. Track the post count and
    // announce on each new batch via the polite live region so AT users
    // know how many posts were just loaded.
    const announce = useAnnouncer();
    const previousCountRef = useRef(postsList?.length || 0);

    // a11y(SMASH-1377 F3): when the final batch loads, showLoadButton flips
    // false and the whole <section> (including the focused button) unmounts,
    // dropping keyboard focus to <body> (WCAG 2.4.3). Record at click time
    // whether focus was inside the section and where the new posts will start;
    // if the button is gone once the batch lands, move focus to the first
    // newly loaded post instead.
    const sectionRef = useRef(null);
    const pendingFocusRef = useRef(null);

    useEffect(() => {
        const firstNewIndex = pendingFocusRef.current;
        if (firstNewIndex === null || firstNewIndex === undefined) return;
        const currentCount = postsList?.length || 0;
        if (currentCount <= firstNewIndex) return; // batch not rendered yet
        pendingFocusRef.current = null;
        if (sectionRef.current) return; // button survived — focus is intact
        const container = document.getElementById(`sb-tiktok-feeds-container-${feedId}`);
        if (!container) return;
        const target = container.querySelectorAll('button.sb-post-item-image-wrap')[firstNewIndex]
            || container.querySelectorAll('.sb-post-item-wrap')[firstNewIndex];
        if (target) {
            if (target.tagName !== 'BUTTON') {
                target.setAttribute('tabindex', '-1');
            }
            target.focus();
        }
    }, [postsList, feedId]);

    useEffect(() => {
        if (isFeedEditor) return;
        const currentCount = postsList?.length || 0;
        const delta = currentCount - previousCountRef.current;
        if (delta > 0) {
            announce(
                sprintf(
                    /* translators: %d is the number of newly loaded posts. */
                    _n('Loaded %d more post', 'Loaded %d more posts', delta, 'sb-customizer'),
                    delta
                )
            );
        }
        previousCountRef.current = currentCount;
    }, [postsList, isFeedEditor, announce]);

    useEffect(() => {
        if (!isFeedEditor) {
            setOffset(postsPerPage);
            setLoadCount(1);
            setPostsList(posts?.slice(0, postsPerPage));
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postsPerPage]);

    const loadMorePosts = async () => {

        // a11y(SMASH-1377 F3): remember the focus state + first-new-post index
        // before the load mutates state (see redirect effect above).
        pendingFocusRef.current = sectionRef.current?.contains(document.activeElement)
            ? (postsList?.length || 0)
            : null;

        if (posts?.length >= postsPerPage * (loadCount + 1)) {
            setPostsList(prevPosts => [...prevPosts, ...posts.slice(offset, offset + postsPerPage)]);
        }

        else {
            const pageNumber = page + 1;
            setPage(pageNumber);

            const newPosts = await SbUtils.loadMorePosts(feedId, pageNumber);

            if (newPosts?.nextPage) {
                setNextPage(newPosts.nextPage);
            } else {
                setNextPage(null);
            }
            setPosts(prevPosts => [...prevPosts, ...newPosts?.posts]);
        }

        setOffset(offset + postsPerPage);
        setLoadCount(loadCount + 1);
    }

    useEffect(() => {
        if (posts && posts?.length > 0) {
            const newPostsList = [...posts.slice(0, loadCount * postsPerPage)];
            setPostsList(newPostsList);
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [posts]);

    useEffect(() => {
        if (posts?.length > postsList?.length) {
            return setShowLoadButton(true);
        }
        else {
            return setShowLoadButton(false)
        }

    }, [posts, postsList]);

    return (
        feedSettings?.showLoadButton === true &&
        layout !== 'carousel' &&
        (isFeedEditor || nextPage || showLoadButton) &&
        <section className='sb-load-button-ctn sb-fs' ref={sectionRef}>
            <Button
                text={feedSettings?.loadButtonText}
                full-width={true}
                customClass='sb-load-button'
                size='small'
                onClick={!isFeedEditor ? loadMorePosts : null}
            />
            {isFeedEditor && SbUtils.addHighlighter('loadmore-button', 'Load More Button')}
        </section>
    )
}

export default LoadMoreButton;
